import { resolveApiUrl } from "../config/environment";
import { isStandalone } from "../pwa/install";
import {
  collectPushDiagnostics,
  currentPlatform,
  pushPermission,
  type BrowserPushState,
  type PushDiagnosticsOptions,
} from "../web-push";
import { fingerprint } from "../web-push/WebPushManager";
import type { ShellServices } from "./services";

type DiagnosticsServices = ShellServices & Required<Pick<ShellServices, "notifications">>;

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Состояние этого устройства: разрешение, service worker, подписка. Адрес подписки наружу не отдаём. */
export async function collectBrowserPushState(): Promise<BrowserPushState> {
  const workerSupported = "serviceWorker" in navigator;
  const registration = workerSupported ? await navigator.serviceWorker.getRegistration("/") : undefined;
  const subscription =
    registration !== undefined && "PushManager" in window ? await registration.pushManager.getSubscription() : null;
  const key = subscription?.options.applicationServerKey ?? null;
  let host: string | null = null;
  if (subscription !== null) {
    try {
      host = new URL(subscription.endpoint).host;
    } catch {
      host = null;
    }
  }

  return {
    platform: currentPlatform(),
    installedApp: isStandalone(),
    permission: pushPermission(),
    workerActive: registration?.active?.state === "activated",
    subscription:
      subscription === null
        ? null
        : {
            fingerprint: await fingerprint(subscription.endpoint),
            pushService: host,
            applicationServerKey: key === null ? null : base64Url(new Uint8Array(key)),
          },
  };
}

/**
 * Читает ответ сервера как есть, без строгой проверки формата.
 *
 * Клиент приложения отбрасывает ответ, не подходящий под схему, — именно так
 * outbox-health скрывал своё содержимое за «invalid success response». Для
 * диагностики важнее увидеть данные, пусть даже неожиданной формы.
 */
async function rawData(path: string): Promise<unknown> {
  const response = await fetch(resolveApiUrl(path), { credentials: "include", headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  return ((await response.json()) as { data?: unknown }).data;
}

/** Собирает отчёт о доставке push (см. `collectPushDiagnostics`) на настоящих сервисах приложения. */
export function createPushDiagnostics(services: DiagnosticsServices) {
  const api = services.notifications;
  const operations = services.operations;

  return (options?: PushDiagnosticsOptions) =>
    collectPushDiagnostics(
      {
        browser: collectBrowserPushState,
        vapidPublicKey: async () => (await api.realtimeConfig()).data.vapid_public_key,
        generalWebPush: async () =>
          (await api.preferences()).data.preferences.find((row) => row.type === "*")?.web_push ?? null,
        // Список читаем как есть: причина отказа push-службы (last_error) есть в ответе
        // сервера, но не в схеме клиента, и через него она бы отбрасывалась.
        subscriptions: async () => {
          const rows = (await rawData("/api/v1/push-subscriptions")) as {
            id: string;
            device_name: string | null;
            endpoint_fingerprint: string;
            last_used_at: string | null;
            last_error?: { at: string; summary: string } | null;
          }[];

          return rows.map((device) => ({
            id: device.id,
            name: device.device_name ?? "устройство без имени",
            fingerprint: device.endpoint_fingerprint,
            lastDeliveredAt: device.last_used_at,
            lastError: device.last_error ?? null,
          }));
        },
        queueHealth: () => rawData("/api/v1/system/queue-health"),
        outboxHealth: () => rawData("/api/v1/system/outbox-health"),
        sendProbe: operations === undefined ? undefined : () => operations.testNotification(),
        sleep: (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds)),
        now: () => new Date(),
      },
      options,
    );
}

/**
 * Делает диагностику доступной из консоли браузера: `await erpPushDiagnostics()`
 * — быстрый отчёт, `await erpPushDiagnostics({ probe: true })` — с живой
 * проверкой (до 30 секунд). Возвращает готовый текст и печатает его же, чтобы
 * его можно было скопировать (в Chrome и Edge — `copy(await erpPushDiagnostics())`).
 */
export function installPushDiagnostics(services: DiagnosticsServices): () => void {
  const run = createPushDiagnostics(services);
  const target = window as unknown as { erpPushDiagnostics?: unknown };
  target.erpPushDiagnostics = async (options?: PushDiagnosticsOptions) => {
    const text = JSON.stringify(await run(options), null, 2);
    console.info(text);

    return text;
  };

  return () => {
    delete target.erpPushDiagnostics;
  };
}
